import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Sentry } from './config/sentry';
import { CommonLayout, RaceLayout, RaceUserLayout } from './core/components';
import { AuthProvider } from './core/contexts/AuthContext';

function ErrorFallback({
  resetError,
}: {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 2,
        p: 3,
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" color="error">
        エラーが発生しました
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
        予期しないエラーが発生しました。問題が解決しない場合は、サポートまでお問い合わせください。
      </Typography>
      <Button variant="contained" onClick={resetError}>
        再読み込み
      </Button>
    </Box>
  );
}

const hostname = window.location.hostname;
const isProduction = hostname === 'nft.runport.run';

// Google OAuth Client ID (dot99 project)
const googleClientId = isProduction
  ? '281512048896-8976la8dp9qr60uvv134p31muck38nqt.apps.googleusercontent.com'
  : '1065437798827-2o9p05fjr4424vu0lvv7jarribnrahl5.apps.googleusercontent.com';

import { AdminLayout, AdminLoginPage, FeePage, MintingPage, ResultsPage } from './features/admin';
import { AuthCallbackPage, LoginPage, TwitterCallbackPage } from './features/auth';
import { BoothPage } from './features/booth';
import { ClaimPage } from './features/claim';
import { GalleryPage } from './features/gallery';
import { HistoryPage } from './features/history';
import { LPPage } from './features/lp';
import { ClientsPage, ImportPage, OperatorLayout, RacesPage, WalletsPage } from './features/operator';
import { CertsPage } from './features/public-gallery';
import { WalletPage } from './features/wallet';

const defaultTheme = createTheme();

function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <GoogleOAuthProvider clientId={googleClientId}>
        <ThemeProvider theme={defaultTheme}>
          <CssBaseline />
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<LPPage />} />
                {/* Google OAuth コールバック */}
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                {/* X（Twitter）OAuth コールバック */}
                <Route path="/auth/twitter/callback" element={<TwitterCallbackPage />} />
                {/* 全大会共通画面 */}
                <Route element={<CommonLayout />}>
                  <Route path="/gallery" element={<GalleryPage />} />
                  <Route path="/history" element={<HistoryPage />} />
                  <Route path="/wallet" element={<WalletPage />} />
                </Route>
                {/* 運営管理画面 */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<OperatorLayout />}>
                  <Route index element={<Navigate to="clients" replace />} />
                  <Route path="races" element={<RacesPage />} />
                  <Route path="clients" element={<ClientsPage />} />
                  <Route path="import" element={<ImportPage />} />
                  <Route path="wallets" element={<WalletsPage />} />
                </Route>
                {/* 大会別画面 */}
                <Route path="/:raceAlias" element={<RaceLayout />}>
                  {/* 会場スタッフ向け画面（認証不要） */}
                  <Route path="booth" element={<BoothPage />} />
                  {/* みんなの完走証（認証不要） */}
                  <Route path="certs" element={<CertsPage />} />
                  {/* ユーザー向け画面 */}
                  <Route element={<RaceUserLayout />}>
                    <Route index element={<LoginPage />} />
                    <Route path="claim" element={<ClaimPage />} />
                    <Route path="gallery" element={<GalleryPage />} />
                    <Route path="history" element={<HistoryPage />} />
                    <Route path="wallet" element={<WalletPage />} />
                  </Route>
                  {/* 大会管理画面 */}
                  <Route path="admin/login" element={<AdminLoginPage />} />
                  <Route path="admin" element={<AdminLayout />}>
                    <Route index element={<Navigate to="minting" replace />} />
                    <Route path="minting" element={<MintingPage />} />
                    <Route path="results" element={<ResultsPage />} />
                    <Route path="check" element={<Navigate to="results" replace />} />
                    <Route path="fee" element={<FeePage />} />
                  </Route>
                </Route>
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
